import { ColumnDef, flexRender, getCoreRowModel, Row, RowSelectionState, useReactTable } from '@tanstack/react-table'
import Button from 'components/core/button'
import { Ticket } from 'interfaces/ticket'
import { TicketStatusBadge } from 'lib/components/badge/badge'
import Checkbox from 'lib/components/checkbox/checkbox'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { request } from 'lib/api/fetch-api'
import { Skeleton } from 'lib/components/skeleton/skeleton'
import { useInView } from 'react-intersection-observer'
import { toast } from 'sonner'
import { Brand } from 'interfaces/brand'
import { Project } from 'interfaces/project'
import { BrandColor } from 'components/pages/request/brands/brands'
import DeliverablesTableTitleCell from './deliverables-table-title-cell'
import { cn } from 'lib/util/cn'
import SearchInput from 'lib/components/search-input/search-input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'lib/components/table/table'

const CellWrapper = ({ isSelected, children }: { isSelected: boolean; children: ReactNode }) => (
  <span className={cn({ 'tw-text-neutral-800': isSelected })}>{children}</span>
)

const ProjectCell = ({
  projectId,
  project,
  isSelected,
}: {
  projectId: number
  project: Project
  isSelected: boolean
}) => {
  const trimmedProjectName = project?.name?.length > 40 ? `${project?.name?.substring(0, 40)}...` : project?.name
  if (!project) return <CellWrapper isSelected={isSelected}>-</CellWrapper>
  if (project.id === projectId) return <CellWrapper isSelected={isSelected}>Already in this project</CellWrapper>
  return <CellWrapper isSelected={isSelected}>{trimmedProjectName}</CellWrapper>
}

export const BrandCell = ({ brand, isSelected }: { brand: Brand | null; isSelected: boolean }) => {
  if (!brand) return <CellWrapper isSelected={isSelected}>-</CellWrapper>
  return (
    <CellWrapper isSelected={isSelected}>
      <span>{brand.name}</span>
      <span className="tw-flex tw-flex-row tw-gap-1">
        {brand?.colors
          ?.slice(0, 4)
          ?.map((color) => <BrandColor variant={{ size: 'sm' }} key={color.id} color={color.value} />)}
      </span>
    </CellWrapper>
  )
}

interface AddDeliverablesTableProps {
  projectId?: number
  onFinished: (update?: boolean) => void
  isAdmin?: boolean
  handleAddDeliverables: (ticketIds: number[]) => void
  enableRowSelection?: boolean | ((row: Row<Ticket>) => boolean)
  isChecked?: (row: Row<Ticket>) => boolean
  buttonText?: string
  selectedText?: string
  showCreateButton?: boolean
}

const AddDeliverablesTable = ({
  projectId,
  onFinished,
  isAdmin,
  handleAddDeliverables,
  enableRowSelection,
  isChecked,
  buttonText,
  selectedText,
  showCreateButton = true,
}: AddDeliverablesTableProps) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const { ref, inView } = useInView()

  const [searchValue, setSearchValue] = useState('')

  const columns: ColumnDef<Ticket>[] = [
    {
      id: 'select-col',
      cell: ({ row, table }) => (
        <Checkbox
          className="tw-m-4"
          isChecked={row.getIsSelected() || isChecked?.(row)}
          disabled={!row.getCanSelect()}
          onClick={(e) => {
            row.getToggleSelectedHandler()(e)
            table.setGlobalFilter(row.original.subject)
          }}
        />
      ),
      size: 8,
    },
    {
      id: 'title',
      header: 'Title',
      cell: (props) => <DeliverablesTableTitleCell ticket={props.row.original} isAdmin={isAdmin} />,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'friendlyStatusName',
      cell: (props) => <TicketStatusBadge status={props.cell.getValue() as string} />,
    },
    {
      id: 'project',
      header: 'Project',
      accessorKey: 'project',
      cell: (props) => (
        <ProjectCell
          projectId={projectId}
          project={props.cell.getValue() as Project}
          isSelected={props.row.getIsSelected()}
        />
      ),
    },
    {
      id: 'brand',
      header: 'Brand',
      accessorKey: 'brand',
      cell: (props) => <BrandCell brand={props.cell.getValue() as Brand} isSelected={props.row.getIsSelected()} />,
    },
  ]

  const { data, isLoading, error, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['allTickets', searchValue],
    queryFn: ({ pageParam = 1 }) =>
      request({ endpoint: 'getAllTickets', query: { page: pageParam, subject: searchValue } }),
    getNextPageParam: (lastPage, pages) => (lastPage.meta?.nextPage ? pages.length + 1 : undefined),
    staleTime: 1000,
    retry: 3,
  })

  const tableData = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data?.pages])

  const table = useReactTable({
    columns,
    data: tableData,
    state: {
      rowSelection,
    },
    enableRowSelection: enableRowSelection,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleAddDeliverablesClick = () => {
    const ticketIds = Object.keys(rowSelection).map((key) => tableData[key].id)
    handleAddDeliverables(ticketIds)
  }

  useEffect(() => {
    if (error) {
      toast.error('Failed to fetch requests')
      console.error('Failed to fetch requests', error)
    }
  }, [error])

  useEffect(() => {
    if (inView && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, inView, isFetchingNextPage])

  return (
    <div>
      <div
        className={cn('tw-my-4 tw-flex tw-items-center tw-justify-between tw-gap-2', !showCreateButton && 'tw-mb-4')}
      >
        <SearchInput
          className="tw-flex-1"
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Search requests"
        />
        <h5>Requests ({data?.pages[0].meta.total ?? 0})</h5>
      </div>
      <div>
        <Table>
          <TableHeader className="tw-sticky tw--top-4 tw-z-10">
            <TableRow intent="header">
              {table.getFlatHeaders().map((header) => (
                <TableHead key={header.id} style={{ width: `${header.getSize()}px` }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} intent="body" selected={row.getIsSelected()}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
            <tr ref={ref} className="tw-h-4" />
          </TableBody>
        </Table>
        {(isLoading || isFetchingNextPage) && (
          <Skeleton className="tw-h-24 tw-w-full tw-bg-neutral-200" data-testid="skeleton" />
        )}
      </div>
      <div className="tw-sticky tw-bottom-0 tw-mt-4 tw-flex tw-w-full tw-justify-between tw-gap-2 tw-bg-white tw-py-4">
        <h5>
          {Object.keys(rowSelection).length}{' '}
          {selectedText || `additional request${Object.keys(rowSelection).length !== 1 ? 's' : ''} selected`}
        </h5>
        <div className="tw-flex tw-gap-2">
          <Button onClick={() => onFinished(false)} color="lightGray">
            Cancel
          </Button>
          <Button onClick={handleAddDeliverablesClick} color="purple" disabled={Object.keys(rowSelection).length === 0}>
            {buttonText ?? 'Add requests to project'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AddDeliverablesTable
